import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * `/handoff?token=<jot>` — exchanges the token for a signed-in session
 * (cookies set by the backend) and redirects to Explore. Used by the
 * mobile WebView shell so the app can drop the user straight into the
 * web app already authenticated.
 */
export default function HandoffPage() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { refreshMe } = useAuth();
  const [error, setError] = useState(null);

  useEffect(() => {
    const token = (params.get("token") || "").trim();
    if (!token) {
      setError("Missing handoff token.");
      return;
    }
    (async () => {
      try {
        await api.post("/auth/handoff/exchange", { token });
        await refreshMe();
        // Where to land — respect ?next=... if the caller wanted a specific tab.
        const next = params.get("next") || "/explore";
        nav(next, { replace: true });
      } catch (e) {
        setError(formatApiErrorDetail(e) || "This link expired — request a fresh one.");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      data-testid="handoff-page"
      className="min-h-screen w-full flex items-center justify-center bg-tb-navy text-tb-cream p-6"
    >
      <div className="max-w-sm w-full text-center">
        <div className="font-serif text-5xl">
          Travel<span className="font-script text-tb-gold-bright">Bio</span>
        </div>
        {!error ? (
          <div className="mt-8 flex flex-col items-center gap-3">
            <Loader2 className="w-6 h-6 animate-spin text-tb-gold-bright" />
            <p className="font-hand italic text-sm text-tb-cream/80">
              Signing you in…
            </p>
          </div>
        ) : (
          <div className="mt-8">
            <p className="text-tb-coral font-hand text-base">{error}</p>
            <button
              data-testid="handoff-back-to-login"
              onClick={() => nav("/auth", { replace: true })}
              className="mt-6 px-5 py-2 rounded-xl bg-tb-cream text-tb-navy font-hand"
            >
              back to sign-in →
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
